import { useAuth } from 'context/Auth';
import useOnceInViewport from 'hooks/useOnceInViewport';
import useTrackingMonitorHighMargin from 'hooks/useTrackingMonitorHighMargin';
import useTrackingMonitorSubstituteProduct from 'hooks/useTrackingMonitorSubstituteProduct';
import { useRouter } from 'next/router';
import { ProductReplace } from 'types/product';
import gtag, { mapPathToCategory } from 'utils/gtag';
import { getResultPage, mapMainSkuStatus } from 'utils/TrackingHelpers';

interface Params {
  skuReplace: ProductReplace;
  mainSKUSellerId?: number;
  mainSKUProductId?: string;
  mainSKU?: string;
  mainType?: string;
}

// Tracking của card SPTT (impression, click chi tiết, Xem thêm, add to cart + lợi nhuận cao).
// Tách khỏi SubstituteSKUCard để tile SPTT trong block "Gợi ý cho bạn" (SuggestProductBlock) dùng chung.
export default function useSubstituteCardTracking({ skuReplace, mainSKUSellerId, mainSKUProductId, mainSKU, mainType }: Params) {
  const { productId, sellerId, skuCode, errorCode, isHighProfit } = skuReplace;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { user }: any = useAuth();
  const router = useRouter();
  const {
    trackingMonitorSubstituteClickMore,
    trackingMonitorSubstituteClickDetail,
    trackingMonitorSubstituteMainSKUImpression,
    trackingMonitorSubstituteAddToCart,
  } = useTrackingMonitorSubstituteProduct();
  const { trackingHighMarginImpression, trackingHighMarginClickDetail, trackingHighMarginAddToCart } = useTrackingMonitorHighMargin();

  const gtagPayload = () => ({
    screen: mapPathToCategory(window.location.pathname),
    customerId: user?.customerID,
    sku: skuCode,
    sellerId,
    productId,
    resultPage: getResultPage(router),
    mainSkuStatus: mapMainSkuStatus(mainType),
  });
  const monitorPayload = () => ({
    sku: skuCode,
    sellerId: sellerId?.toString() || '',
    sellerCode: '',
    productId: productId?.toString() || '',
    type: errorCode || 'Instock',
    pathname: router.pathname,
    resultPage: getResultPage(router),
    sellerIdMainSKU: mainSKUSellerId?.toString() || '',
    productIdMainSKU: mainSKUProductId || '',
    mainSKU: mainSKU || '',
    mainType: mainType || 'NORMAL',
  });
  const highMarginGtagPayload = () => ({
    customerId: user?.customerID,
    screen: mapPathToCategory(window.location.pathname),
    resultPage: getResultPage(router),
    sku: skuCode,
    sellerId,
    productId,
  });
  const highMarginMonitorPayload = () => ({
    sku: skuCode,
    sellerId: sellerId?.toString() || '',
    productId: productId?.toString() || '',
    pathname: router.pathname,
    resultPage: getResultPage(router),
  });

  const handleDetailTracking = () => {
    gtag.substituteClickDetail(gtagPayload());
    trackingMonitorSubstituteClickDetail(monitorPayload());
    if (isHighProfit) {
      gtag.highMarginClickDetail(highMarginGtagPayload());
      trackingHighMarginClickDetail(highMarginMonitorPayload());
    }
  };

  const handleShowMoreTracking = () => {
    gtag.substituteClickMore(gtagPayload());
    trackingMonitorSubstituteClickMore(monitorPayload());
  };

  // Event "Add substitute product to cart": bắn khi bấm Add to Cart / nhập số lượng trên card (BV-1669)
  const handleAddToCartTracking = () => {
    gtag.substituteClickAddToCart(gtagPayload());
    trackingMonitorSubstituteAddToCart(monitorPayload());
    if (isHighProfit) {
      gtag.highMarginAddToCart(highMarginGtagPayload());
      trackingHighMarginAddToCart(highMarginMonitorPayload());
    }
  };

  // Event "Search result having substitute product impression": 1 lần khi card hiện >= 50% viewport (spec MKT)
  const impressionRef = useOnceInViewport(() => {
    gtag.substituteSearchResult(gtagPayload());
    trackingMonitorSubstituteMainSKUImpression(monitorPayload());
    if (isHighProfit) {
      gtag.highMarginImpression(highMarginGtagPayload());
      trackingHighMarginImpression(highMarginMonitorPayload());
    }
  }, 0.5);

  return { impressionRef, handleDetailTracking, handleShowMoreTracking, handleAddToCartTracking };
}
